import React, { useState, useRef, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const MessageInput = ({ 
  onSendMessage, 
  disabled = false, 
  placeholder = "How can I help you?", 
  isTyping = false, 
  onCancelRequest 
}) => {
  const [message, setMessage] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textareaRef = useRef(null);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef?.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef?.current?.scrollHeight, 120)}px`;
    }
  }, [message]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    const trimmedMessage = message?.trim();
    if (!trimmedMessage || isSubmitting || disabled) return;

    setIsSubmitting(true);
    
    try {
      await onSendMessage(trimmedMessage);
      setMessage('');
      
      // Reset textarea height after clearing message
      if (textareaRef?.current) {
        textareaRef.current.style.height = 'auto';
      }
    } catch (error) {
      console.log('Error sending message:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (onCancelRequest) {
      onCancelRequest();
    }
  };

  const handleKeyPress = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSubmit(e);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="p-6">
      <div className="relative flex items-end space-x-4 max-w-4xl mx-auto">
        
        {/* Enhanced Input Area with Better Styling */}
        <div className="flex-1 relative">
          <textarea
            ref={textareaRef}
            value={message}
            onChange={(e) => setMessage(e?.target?.value)}
            onKeyPress={handleKeyPress}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            placeholder={placeholder}
            disabled={disabled || isSubmitting}
            rows={1}
            style={{
              minHeight: '60px',
              maxHeight: '200px'
            }}
            className={`w-full px-6 py-4 text-lg rounded-2xl border-2 resize-none transition-all duration-300 focus:outline-none focus:ring-0 ${
              disabled 
                ? 'bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed' :'bg-white border-slate-300 focus:border-blue-500 hover:border-slate-400'
            }`}
          />
          
          {/* Removed "Search limit reached" indicator */}
        </div>
        
        {/* Button Group - Send/Cancel */}
        <div className="flex space-x-2">
          {/* Cancel Button - Show when typing */}
          {isTyping && (
            <Button
              type="button"
              onClick={handleCancel}
              className="px-6 py-4 rounded-2xl font-semibold transition-all duration-300 bg-red-500 hover:bg-red-600 text-white shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
            >
              <div className="flex items-center space-x-2">
                <Icon name="X" size={18} />
                <span>Cancel</span>
              </div>
            </Button>
          )}

          {/* Send Button - Show when not typing */}
          {!isTyping && (
            <Button
              type="submit"
              disabled={!message?.trim() || isSubmitting || disabled}
              className={`px-8 py-4 rounded-2xl font-semibold transition-all duration-300 ${
                disabled
                  ? 'bg-gray-400 cursor-not-allowed' :'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]'
              }`}
            >
              {isSubmitting ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Chatting...</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <Icon name="Search" size={18} />
                  <span>Chat</span>
                </div>
              )}
            </Button>
          )}
        </div>
      </div>
    </form>
  );
};

export default MessageInput;